import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Settings2, Trophy, ChevronDown, Shield, ClipboardCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { syShort } from "@/components/LetterProgressBar";
import { PATH_HOW, progressParts } from "@/lib/letterPolicy";
import api, { formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import PageHeader from "@/components/PageHeader";
import SubmissionReviewCard, { ReviewActions, EMPTY_TEXT } from "@/components/review/SubmissionReviewCard";
import { SubmissionCells, SUBMISSION_COLUMNS } from "@/components/lettering/SubmissionCells";
import { useFeatureFlags } from "@/lib/featureFlags";
import { useAuth } from "@/context/AuthContext";
import { shortNo } from "@/lib/caseNo";
import ReportExportDialog from "@/components/ReportExportDialog";

const OPEN = ["pending", "returned_for_correction"];
const apiError = (err) => toast.error(formatApiError(err.response?.data?.detail));
const REVIEW_STATUSES = [["pending", "Pending"], ["approved", "Approved"], ["declined", "Declined"]];
const LETTER_REPORTS = [
  { key: "submissions", label: "Lettering Submissions", endpoint: "/coach/exports/lettering-submissions", dateLabel: "Event", statuses: REVIEW_STATUSES,
    description: "Every lettering submission with achievement, points, description and status.", hint: "Leave dates empty to include every submission." },
  { key: "progress", label: "Lettering Progress", endpoint: "/coach/exports/lettering-progress",
    description: "Each team member's approved points and First / Subsequent Letter status against your current Lettering Award Policy.", hint: "Covers the whole team as of today." },
];

// Server-paged review list for one filter (sorted by member last name); loads 50 more as the coach scrolls.
function useLetteringRows(filter) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [nextSkip, setNextSkip] = useState(null);
  const [loading, setLoading] = useState(false);
  const gen = useRef(0);
  const busy = useRef(false);

  const fetchRows = useCallback(async (f, skip) => {
    const mine = gen.current;
    busy.current = true; setLoading(true);
    try {
      const { data } = await api.get("/coach/lettering", { params: { status: f, skip, limit: 50 } });
      if (mine !== gen.current) return;
      setRows((prev) => (skip ? [...prev, ...data.items] : data.items));
      setCounts(data.counts || {}); setNextSkip(data.next_skip ?? null);
    } catch (err) { if (mine === gen.current) apiError(err); }
    finally { if (mine === gen.current) { busy.current = false; setLoading(false); } }
  }, []);
  const reload = useCallback(() => { gen.current += 1; busy.current = false; fetchRows(filter, 0); }, [filter, fetchRows]);
  useEffect(() => { reload(); }, [reload]);
  const loadMore = useCallback(() => { if (!busy.current && nextSkip != null) fetchRows(filter, nextSkip); }, [filter, nextSkip, fetchRows]);
  return { rows, counts, hasMore: nextSkip != null, loading, reload, loadMore };
}

export default function LetteringReview() {
  const navigate = useNavigate();
  const { plan } = useFeatureFlags();
  const { viewOnly } = useAuth();
  const [filter, setFilter] = useState("pending");
  const { rows, counts, hasMore, loading, reload, loadMore } = useLetteringRows(filter);
  const [members, setMembers] = useState([]);
  const [approving, setApproving] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [comment, setComment] = useState("");

  const loadMembers = useCallback(async () => {
    try { const { data: tm } = await api.get("/coach/team-members"); setMembers(tm.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadMembers(); }, [loadMembers]);
  const refresh = () => { reload(); loadMembers(); window.dispatchEvent(new Event("rpg:badges-refresh")); };

  const act = async (s, action, msg) => {
    try { await api.post(`/coach/lettering/${s.id}/${action}`); toast.success(msg); refresh(); }
    catch (err) { apiError(err); }
  };
  const doApprove = async () => { const s = approving; setApproving(null); if (s) await act(s, "approve", "Submission approved"); };

  const doReturn = async () => {
    if (!comment.trim()) { toast.error("Please write a reason for returning"); return; }
    try {
      await api.post(`/coach/lettering/${returnFor.id}/return`, { comment });
      toast.success("Returned for correction");
      setReturnFor(null); setComment(""); refresh();
    } catch (err) { apiError(err); }
  };

  return (
    <div>
      <PageHeader title="Letter Points Review" subtitle="Review lettering submissions for your team"
        actions={<div className="flex flex-wrap gap-2">
          <ReportExportDialog title="Export lettering" reports={LETTER_REPORTS} accent="violet" testId="lettering-export"
            members={members.map((m) => ({ id: m.id, name: `${m.first_name} ${m.last_name}` }))} />
          {plan !== "base" && <Button variant="outline" className="rounded-full" onClick={() => navigate("/coach/achievements")} data-testid="coach-achievement-options-btn"><Settings2 className="mr-2 h-4 w-4" /> Settings</Button>}
        </div>} />

      <SubmissionReviewCard title="Lettering Submissions" icon={ClipboardCheck} iconClass="bg-violet-100 text-violet-700 ring-1 ring-violet-200"
        filter={filter} onFilter={setFilter} counts={counts} columns={SUBMISSION_COLUMNS} rowCount={rows.length}
        emptyText={EMPTY_TEXT[filter]} hasMore={hasMore} loading={loading} onLoadMore={loadMore} testId="lettering-review">
        {rows.map((s) => (
          <TableRow key={s.id} data-testid={`review-row-${s.case_number}`}>
            <SubmissionCells s={s}>
              {!viewOnly && OPEN.includes(s.status) ? (
                <ReviewActions onApprove={() => setApproving(s)} onDecline={() => act(s, "disapprove", "Submission declined")}
                  onReturn={() => { setReturnFor(s); setComment(""); }}
                  testIds={{ approve: `approve-lettering-${s.case_number}`, decline: `disapprove-lettering-${s.case_number}`, return: `return-lettering-${s.case_number}` }} />
              ) : <span className="text-xs text-slate-400">Locked</span>}
            </SubmissionCells>
          </TableRow>
        ))}
      </SubmissionReviewCard>

      <LetteringProgress members={members} />

      <ConfirmDialog
        open={!!approving} onOpenChange={(o) => !o && setApproving(null)}
        title="Approve submission?"
        description={approving ? `${shortNo(approving.case_number)} will be approved and locked.` : ""}
        confirmText="Approve" onConfirm={doApprove} testId="review-confirm-dialog"
      />

      <Dialog open={!!returnFor} onOpenChange={(o) => !o && setReturnFor(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Return for correction</DialogTitle>
            <DialogDescription>{shortNo(returnFor?.case_number)} goes back to {returnFor?.team_member_name} to fix and resubmit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason (required)</Label>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="return-comment-input" placeholder="Explain what needs correcting…" />
          </div>
          <DialogFooter>
            <Button onClick={doReturn} className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="return-submit-btn">Return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LetteringProgress({ members = [] }) {
  const [open, setOpen] = useState(false);
  if (!members.length) return null;

  const firstLetterCell = (ls) => {
    if (!ls) return <span className="text-slate-400">—</span>;
    if (ls.first_letter_earned) {
      const how = PATH_HOW[ls.first_letter_path] || "earned";
      return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid="progress-first-earned"><Trophy className="h-3 w-3" /> Earned <span className="font-normal text-emerald-600">· {how}</span></span>;
    }
    return <span className="text-xs text-slate-600">{progressParts(ls.progress?.first).join(" or ") || "Coach award only"}</span>;
  };

  const nextAwardCell = (ls) => {
    if (!ls) return <span className="text-slate-400">—</span>;
    if (!ls.first_letter_earned) return <span className="text-xs text-slate-400">After first letter</span>;
    if (ls.additional_eligible_this_year === false) {
      return <span className="text-xs text-slate-500" data-testid="progress-additional-next-year">{ls.additional_awards} earned · starts {syShort(ls.additional_eligible_from)}</span>;
    }
    return (
      <span className="text-xs text-slate-600">
        <span className="font-semibold text-violet-700">{ls.additional_awards}</span> earned
        {" · "}{ls.this_year_awarded ? "this year's earned" : (progressParts(ls.progress?.subsequent).join(" or ") || "coach award only")}
      </span>
    );
  };

  return (
    <Card className="mb-4 border-slate-200 shadow-sm" data-testid="lettering-progress-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50" data-testid="lettering-progress-toggle" aria-expanded={open}>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-700 ring-1 ring-violet-200" data-testid="lettering-progress-icon"><Shield className="h-4 w-4" /></span>
        <div>
          <h3 className="font-heading text-sm font-bold text-slate-800">Team Lettering Progress</h3>
          <p className="text-xs text-slate-500">Live status recalculated against your current Lettering Award Policy.</p>
        </div>
        <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="overflow-x-auto">
        <Table data-testid="lettering-progress-table">
          <TableHeader><TableRow className="bg-slate-50">{["Member", "Total pts", "This year", "First Letter", "Subsequent Letter"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {members.map((m) => {
              const ls = m.letter_status || {};
              return (
                <TableRow key={m.id} data-testid={`progress-row-${m.id}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{m.first_name} {m.last_name}</TableCell>
                  <TableCell className="font-bold tabular-nums text-violet-600">{ls.total_approved_points ?? 0}</TableCell>
                  <TableCell className="tabular-nums text-slate-600">{ls.points_this_year ?? 0}</TableCell>
                  <TableCell>{firstLetterCell(ls)}</TableCell>
                  <TableCell>{nextAwardCell(ls)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}
    </Card>
  );
}
